import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { IconTextButton } from "../../components/ButtonComponents";
import { CustomSelectInput } from "../../components/CustomSelectInput";
import { Plus, Pencil, Trash2, Save, Settings, Search } from "lucide-react";
import { useSettingsStore } from "../../store/settingsStore";
import { useBankStore } from "../../store/bankStore";
import { useSelectDialog } from "../../hooks/useSelectDialog";
import { Bank } from "../../types/bank";
import { Filter } from "../../types";

export const SanalPosConfig = () => {
  const { paymentConfigs, savePayment, deletePayment, fetchSettings } = useSettingsStore();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState<any>(null);

  const { fetchDataDynamic: fetchBanks, loading: bankLoading } = useBankStore();
  const bankSelect = useSelectDialog<Bank>();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleOpenBankSelect = async () => {
    const res = await fetchBanks({ pageIndex: 1, pageSize: 10 });
    const selected = await bankSelect.open({
      title: "Tahsilat Bankası Seç",
      data: res.items || [],
      loading: bankLoading,
      columns: [
        { field: "name", header: "Banka/Şube" },
        { field: "iban", header: "IBAN" },
        { field: "currency", header: "Döviz" }
      ],
      onFilter: async (p) => {
        const r = await fetchBanks({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });

    if (selected) {
      setEditingPayment((prev: any) => ({
        ...prev,
        bankId: selected.id,
        bankName: selected.name
      }));
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="hidden md:flex items-center justify-between sticky top-0 z-50 bg-bgAlt backdrop-blur-md -mx-4 -mt-4 p-3 border-b border-borderColor shadow-sm mb-6 rounded-t-lg">
        <div className="flex flex-col">
          <h3 className="font-bold text-sm text-text md:block">Sanal POS Yönetimi</h3>
          <p className="text-[11px] text-text opacity-60 hidden md:block">Müşterilerinizin online ödeme yapacağı POS altyapılarını yönetin.</p>
        </div>
        <IconTextButton icon={Plus} className="btn-new shadow-md whitespace-nowrap" onClick={() => {
          setEditingPayment({ isActive: true, priority: 1, minAmount: 0, provider: 'PAYTR' });
          setShowPaymentDialog(true);
        }}>Yeni POS Ekle</IconTextButton>
      </div>

      <div className="card2 p-0 overflow-hidden">
        <DataTable value={paymentConfigs} className="text-sm shadow-sm border-none" responsiveLayout="scroll">
          <Column field="name" header="Tanım" />
          <Column field="provider" header="Sağlayıcı" body={(r) => <Tag value={r.provider} severity="info" />} />
          <Column field="bank.name" header="Aktarılacak Banka" body={(r) => r.bank?.name || "Belirtilmemiş"} />
          <Column field="priority" header="Sıra" className="w-20" />
          <Column header="Durum" body={(r) => r.isDefault ? <Tag severity="success" value="Varsayılan" /> : <Tag severity="secondary" value="Pasif" />} />
          <Column header="İşlemler" body={(row) => (
            <div className="flex gap-2">
              <button onClick={() => { setEditingPayment({ ...row, bankName: row.bank?.name }); setShowPaymentDialog(true); }} className="p-2 hover:bg-bgAlt border border-borderColor rounded-full text-primary transition-colors"><Pencil size={16} /></button>
              <button onClick={() => deletePayment(row.id)} className="p-2 hover:bg-red-100 rounded-full text-red-600 transition-colors"><Trash2 size={16} /></button>
            </div>
          )} />
        </DataTable>
      </div>

      <Dialog
        header="Sanal POS Yapılandırması"
        visible={showPaymentDialog}
        style={{ width: '550px' }}
        onHide={() => setShowPaymentDialog(false)}
        footer={(
          <div className="flex justify-end gap-2 p-2">
            <IconTextButton icon={Save} className="btn-new" onClick={async () => {
              const success = await savePayment(editingPayment);
              if (success) setShowPaymentDialog(false);
            }}>Kaydet</IconTextButton>
          </div>
        )}
      >
        {editingPayment && (
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs font-bold">Kanal İsmi (Müşteriye Gözükür)</label>
              <InputText value={editingPayment.name || ""} onChange={(e) => setEditingPayment({ ...editingPayment, name: e.target.value })} placeholder="Örn: Bonus Kredi Kartı POS" />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold">Sağlayıcı</label>
              <Dropdown value={editingPayment.provider} options={['PAYTR', 'KUVEYTTURK']} onChange={(e) => setEditingPayment({ ...editingPayment, provider: e.value })} />
            </div>

            <div className="flex flex-col gap-1">
              <CustomSelectInput
                label="Hedef Banka Hesabı"
                value={editingPayment.bankName || ""}
                placeholder="Seçiniz..."
                icon={Search}
                onClick={handleOpenBankSelect}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold">Öncelik Sırası</label>
              <InputNumber value={editingPayment.priority} onValueChange={(e) => setEditingPayment({ ...editingPayment, priority: e.value })} min={1} />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold">Min. İşlem Tutarı</label>
              <InputNumber value={editingPayment.minAmount} onValueChange={(e) => setEditingPayment({ ...editingPayment, minAmount: e.value })} mode="currency" currency="TRY" />
            </div>

            <div className="flex items-center justify-between col-span-2 border p-3 rounded-xl bg-bgAlt/30">
              <div className="flex flex-col">
                <span className="text-sm font-bold">Taksit Özelliği</span>
                <span className="text-[10px] opacity-60">Bu kanal üzerinden taksitli ödeme alınabilsin mi?</span>
              </div>
              <InputSwitch checked={editingPayment.allowInstallments} onChange={(e) => setEditingPayment({ ...editingPayment, allowInstallments: e.value })} />
            </div>

            <div className="flex items-center justify-between col-span-2 border p-3 rounded-xl bg-bgAlt/30">
              <div className="flex flex-col">
                <span className="text-sm font-bold">Varsayılan Yap</span>
                <span className="text-[10px] opacity-60">Kredi kartı ödemelerinde ilk bu kanal seçili gelir.</span>
              </div>
              <InputSwitch checked={editingPayment.isDefault} onChange={(e) => setEditingPayment({ ...editingPayment, isDefault: e.value })} />
            </div>
            <div className="flex items-center justify-between col-span-2 border p-3 rounded-xl bg-bgAlt border border-borderColor border-borderColor">
              <div className="flex flex-col">
                <span className="text-sm font-bold text-secondary">Test Modu (Sandbox)</span>
                <span className="text-[10px] opacity-60">Aktif edilirse gerçek işlem yapılmaz, test kartları kullanılır.</span>
              </div>
              <InputSwitch
                checked={editingPayment.isTestMode || false}
                onChange={(e) => setEditingPayment({ ...editingPayment, isTestMode: e.value })}
              />
            </div>
            <div className="col-span-2 border-t mt-2 pt-4 space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-tighter opacity-50 flex items-center gap-2"><Settings size={12} /> Teknik API Parametreleri</h4>
              {editingPayment.provider === 'PAYTR' ? (
                <div className="space-y-2">
                  <InputText value={editingPayment.merchantId || ""} onChange={(e) => setEditingPayment({ ...editingPayment, merchantId: e.target.value })} placeholder="Merchant ID" className="w-full text-sm" />
                  <InputText value={editingPayment.merchantKey || ""} onChange={(e) => setEditingPayment({ ...editingPayment, merchantKey: e.target.value })} placeholder="Merchant Key" className="w-full text-sm" />
                  <InputText value={editingPayment.merchantSalt || ""} onChange={(e) => setEditingPayment({ ...editingPayment, merchantSalt: e.target.value })} placeholder="Merchant Salt" className="w-full text-sm" />
                </div>
              ) : (
                <div className="space-y-2">
                  <InputText value={editingPayment.merchantId || ""} onChange={(e) => setEditingPayment({ ...editingPayment, merchantId: e.target.value })} placeholder="Customer No" className="w-full text-sm" />
                  <InputText value={editingPayment.merchantKey || ""} onChange={(e) => setEditingPayment({ ...editingPayment, merchantKey: e.target.value })} placeholder="Merchant ID" className="w-full text-sm" />
                  <InputText value={editingPayment.apiKey || ""} onChange={(e) => setEditingPayment({ ...editingPayment, apiKey: e.target.value })} placeholder="API User" className="w-full text-sm" />
                  <InputText type="password" value={editingPayment.apiSecret || ""} onChange={(e) => setEditingPayment({ ...editingPayment, apiSecret: e.target.value })} placeholder="API Password" title="KuveytTürk API Şifresi" className="w-full text-sm" />
                </div>
              )}
            </div>

          </div>
        )}
      </Dialog>
      {bankSelect.modal}
    </div>
  );
};
